/**
 * Triagem dos arquivos soltos do computador (spec 2026-10-19-kb-arrastar-e-soltar, D6):
 * o que a tela já sabe que o servidor recusaria não sobe. O servidor confere de novo,
 * por magic bytes e pelo `KB_MAX_UPLOAD_MB`.
 */

import { verificarKbDocumento, type KbVerificacao, type SituacaoDoArquivo } from '@/api/kb'

/** Decisão da spec (D6): protege de soltar a pasta Downloads inteira sem querer. */
export const MAX_ARQUIVOS_POR_SOLTURA = 20
/**
 * O `KB_MAX_UPLOAD_MB` padrão (`assistant_kb_config.py`). Vale só até a resposta de
 * `GET /assistant/kb/limites` chegar (`useLimitesDaKb`): quem decide é a api.
 */
export const MAX_MB_PADRAO = 100

export const MIME_ACCEPT = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/markdown',
  'text/csv',
  'image/png',
  'image/jpeg',
  'image/webp',
].join(',')

/** As extensões dos tipos de `MIME_ACCEPT`: o arquivo solto nem sempre traz o tipo. */
export const EXTENSOES_ACEITAS: readonly string[] = ['pdf', 'docx', 'xlsx', 'pptx', 'txt', 'md', 'csv', 'png', 'jpg', 'jpeg', 'webp']

export const MOTIVO_DIRETORIO = 'Pastas do computador não são enviadas: abra a pasta e solte os arquivos.'

export interface ItemDeEnvio { arquivo: File; recusa: string | null }
export interface Triagem { itens: ItemDeEnvio[]; diretorios: number; excedeu: boolean }
type Entrada = { file: File | null; diretorio: boolean }

export function entradasDoDrop(dt: DataTransfer): Entrada[] {
  const itens = Array.from(dt.items ?? [])
  if (itens.length === 0 || itens.some((i) => typeof i.webkitGetAsEntry !== 'function')) {
    return Array.from(dt.files ?? []).map((file) => ({ file, diretorio: false }))
  }
  return itens
    .filter((i) => i.kind === 'file')
    .map((i) => (i.webkitGetAsEntry()?.isDirectory ? { file: null, diretorio: true } : { file: i.getAsFile(), diretorio: false }))
}

export function recusaDe(f: File, maxMb: number = MAX_MB_PADRAO): string | null {
  const ext = f.name.includes('.') ? f.name.split('.').pop()!.toLowerCase() : ''
  if (!EXTENSOES_ACEITAS.includes(ext)) return 'Tipo de arquivo não suportado.'
  if (f.size > maxMb * 1024 * 1024) return `Arquivo maior que o limite de ${maxMb} MB.`
  return null
}

export function triar(entradas: Entrada[], maxMb: number = MAX_MB_PADRAO): Triagem {
  const diretorios = entradas.filter((e) => e.diretorio).length
  const itens = entradas
    .filter((e): e is { file: File; diretorio: false } => !e.diretorio && e.file !== null)
    .map((e) => ({ arquivo: e.file, recusa: recusaDe(e.file, maxMb) }))
  return { itens, diretorios, excedeu: itens.length > MAX_ARQUIVOS_POR_SOLTURA }
}

// ---------------------------------------------------------------------------
// Arquivo que já está na base (spec 2026-10-22-kb-arquivo-repetido-e-atalho, §4.4)
// ---------------------------------------------------------------------------

function bytesDoArquivo(f: File): Promise<ArrayBuffer> {
  if (typeof f.arrayBuffer === 'function') return f.arrayBuffer()
  // `File.arrayBuffer` não existe em jsdom nem em navegador antigo.
  return new Promise((ok, falha) => {
    const leitor = new FileReader()
    leitor.onload = () => ok(leitor.result as ArrayBuffer)
    leitor.onerror = () => falha(leitor.error)
    leitor.readAsArrayBuffer(f)
  })
}

/**
 * sha256 do conteúdo, em hex: o mesmo que o servidor calcula em `kb_envio.receber`.
 * `crypto.subtle` não é incremental: o arquivo inteiro (até o teto, 100 MB) fica em memória
 * durante o cálculo, e por isso o envio de vários calcula um arquivo por vez (spec, R2).
 */
export async function sha256DoArquivo(f: File): Promise<string> {
  const resumo = await crypto.subtle.digest('SHA-256', new Uint8Array(await bytesDoArquivo(f)))
  return Array.from(new Uint8Array(resumo), (b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Em que situação o arquivo está para a pasta. `null` quando não deu para saber (rede,
 * navegador sem `crypto.subtle`): aí o envio segue, e o 409 do servidor traz o mesmo aviso.
 */
export async function verificarArquivo(pastaId: number, f: File): Promise<KbVerificacao | null> {
  try {
    return await verificarKbDocumento(pastaId, await sha256DoArquivo(f))
  } catch {
    return null
  }
}

/** A recusa do envio por arquivo repetido (409 `documento_ja_existe`), ou `null` para outro erro. */
export function repetidoDoErro(e: unknown): KbVerificacao | null {
  const r = (e as { response?: { status?: number; data?: { detail?: unknown } } })?.response
  const d = r?.data?.detail as { code?: unknown; situacao?: SituacaoDoArquivo; documento?: KbVerificacao['documento'] } | undefined
  if (r?.status !== 409 || !d || typeof d !== 'object' || d.code !== 'documento_ja_existe' || !d.situacao) return null
  return { situacao: d.situacao, documento: d.documento }
}

/** "A › B › Req1" vira "Req1". */
export function nomeDaPasta(caminho: string): string {
  return caminho.split('›').pop()!.trim()
}
